import type {
  PreferredSignInMethodResponse,
  VerifyPasswordForFirebaseTokenResponse,
} from '@/_types/auth';
import { _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { User } from '@/_types/models/user';

// --------------------------------
// Basic Firebase

export const getPreferredSignInMethod = async ({
  username,
}: APIRequest<{
  username: string;
}>): APIResponsePromise<PreferredSignInMethodResponse> =>
  await _callBackend(
    `/sessions/get_preferred_sign_in_method?username=${encodeURIComponent(username)}`,
  );

export const getAuthMethods = async (uid: string): Promise<unknown> =>
  await _callBackend(`/sessions/get_auth_methods?uid=${uid}`);

export const verifyPasswordForFirebaseToken = async ({
  username,
  password,
}: APIRequest<{
  username: string;
  password: string;
}>): APIResponsePromise<VerifyPasswordForFirebaseTokenResponse> =>
  await _callBackend('/sessions/verify_password_for_firebase_token', 'POST', {
    username,
    password,
  });

export const verifyPasswordWithWorkOS = async ({
  username,
  password,
}: APIRequest<{
  username: string;
  password: string;
}>): APIResponsePromise<{
  success: boolean;
  auth_token?: string;
  user?: User;
  requires_2fa?: boolean;
  uid?: string;
  message?: string;
}> =>
  await _callBackend('/sessions/verify_password_with_workos', 'POST', {
    username,
    password,
  });

export const sendPasswordResetEmail = async ({
  email,
}: APIRequest<{
  email: string;
}>): APIResponsePromise<{
  message: string;
  workos_enabled?: boolean;
  error?: string;
}> =>
  await _callBackend('/sessions/send_password_reset_email', 'POST', {
    email,
  });

export const resetPasswordWithToken = async ({
  token,
  email,
  password,
}: APIRequest<{
  token: string;
  email: string;
  password: string;
}>): APIResponsePromise<{
  success: boolean;
  message?: string;
}> =>
  await _callBackend('/sessions/reset_password_with_token', 'POST', {
    token,
    email,
    password,
  });

export const exchangeForFirebaseToken = async ({
  sorabanToken,
}: APIRequest<{
  sorabanToken: string;
}>): APIResponsePromise<{ firebase_token: string }> =>
  _callBackend('/sessions/exchange_for_firebase_token', 'POST', {
    soraban_token: sorabanToken,
  });

export const getCurrentUser = async ({
  token,
}: APIRequest): APIResponsePromise<User> =>
  await _callBackend('/sessions/current_user', 'GET', null, token);

export const resetSession = async ({
  token,
}: APIRequest): APIResponsePromise<{
  success: boolean;
  message: string;
}> => await _callBackend('/sessions/reset_session', 'POST', null, token);

// --------------------------------
// Code Verification

export const sendSMSVerificationCode = async ({
  uid,
}: APIRequest<{
  uid: string;
}>): APIResponsePromise<{ phone: string }> =>
  await _callBackend(
    '/sessions/send_sms_verification_code',
    'POST',
    {
      uid,
    },
    undefined,
    {
      returnResponseAsIs: true,
    },
  );

export const sendEmailVerificationCode = async ({
  uid,
}: APIRequest<{
  uid: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/sessions/send_email_verification_code',
    'POST',
    {
      uid,
    },
    undefined,
    {
      returnResponseAsIs: true,
    },
  );

export const verifyVerificationCode = async ({
  uid,
  verificationCode,
}: APIRequest<{
  uid?: string;
  verificationCode?: string;
}>): Promise<Response> =>
  await _callBackend(
    '/sessions/verify_verification_code',
    'POST',
    {
      uid,
      verification_code: verificationCode,
    },
    undefined,
    {
      returnResponseAsIs: true,
    },
  );

// --------------------------------
// Magic Links

export const sendAuthLink = async ({
  uid,
  accountantId,
  redirect,
}: APIRequest<{
  uid: string;
  accountantId?: number;
  redirect?: string;
}>): APIResponsePromise =>
  await _callBackend('/sessions/send_auth_link', 'POST', {
    uid,
    accountant_id: accountantId,
    redirect,
  });

export const send2FALink = async ({
  uid,
}: APIRequest<{
  uid: string;
}>): APIResponsePromise =>
  await _callBackend('/sessions/send_2fa_link', 'POST', {
    uid,
  });

// --------------------------------
// To be used

export const verifyToken = async ({ token }: APIRequest): APIResponsePromise =>
  await _callBackend('/sessions/verify_token', 'POST', { token });

export const authenticate = async ({ token }: APIRequest): APIResponsePromise =>
  await _callBackend('/sessions/sessions', 'POST', { idToken: token });

export const createUser = async ({ user }: { user: any }): APIResponsePromise =>
  await _callBackend('/sessions/current_user', 'POST', user);

export const logSignIn = async ({
  uid,
  method,
}: {
  uid: string;
  method: string;
}): APIResponsePromise =>
  await _callBackend('/sessions/log_sign_in', 'POST', {
    method,
    uid,
  });

// --------------------------------
// WorkOS Authentication

export const workosCallback = async ({
  code,
}: APIRequest<{
  code: string;
}>): APIResponsePromise<{
  success: boolean;
  auth_token: string;
  user: User;
}> =>
  await _callBackend(
    '/sessions/workos_callback',
    'POST',
    {
      code,
    },
    undefined,
  );

export const authenticateWithSorabanToken = async ({
  sorabanToken,
}: APIRequest<{
  sorabanToken: string;
}>): APIResponsePromise<{
  success: boolean;
  user: User;
}> =>
  await _callBackend(
    '/sessions/authenticate_with_soraban_token',
    'POST',
    {
      soraban_token: sorabanToken,
    },
    undefined,
  );

export const refreshSorabanToken = async ({
  sorabanToken,
}: APIRequest<{
  sorabanToken: string;
}>): APIResponsePromise<{
  success: boolean;
  auth_token: string;
  user: User;
}> =>
  await _callBackend(
    '/sessions/refresh_soraban_token',
    'POST',
    {
      soraban_token: sorabanToken,
    },
    undefined,
  );
